export const REDACTED = '[REDACTED]';

export const SENSITIVE_KEYS: ReadonlySet<string> = new Set([
  'authorization',
  'cookie',
  'set-cookie',
  'refresh_token',
  'access_token',
  'id_token',
  'code',
  'state',
  'token',
  'password',
  'secret',
  'api_key',
  'sentry_auth_token',
  'csrf',
  'csrf_token',
  'xsrf',
  'xsrf_token',
  'pkce',
  'pkce_verifier',
  'code_verifier',
  'nonce',
]);

export function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEYS.has(key.toLowerCase());
}

export function redact(value: unknown, seen: WeakSet<object> = new WeakSet()): unknown {
  if (value === null || typeof value !== 'object') return value;
  if (seen.has(value as object)) return '[Circular]';
  seen.add(value as object);
  if (Array.isArray(value)) {
    return value.map((v) => redact(v, seen));
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = isSensitiveKey(k) ? REDACTED : redact(v, seen);
  }
  return out;
}

// Cookie jar keys: além de SENSITIVE_KEYS, prefixos __Host-/__Secure- e qualquer
// chave contendo "session" carregam tokens de sessão. Usado quando o SDK do Sentry
// popula request.cookies como objeto separado (worker) — no browser o header
// `cookie` (string) já é redactado via SENSITIVE_KEYS.
export function isSensitiveCookieKey(key: string): boolean {
  if (isSensitiveKey(key)) return true;
  const lower = key.toLowerCase();
  return lower.startsWith('__host-') || lower.startsWith('__secure-') || lower.includes('session');
}

export function redactCookies(cookies: unknown): unknown {
  if (cookies === null || typeof cookies !== 'object' || Array.isArray(cookies)) return cookies;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(cookies as Record<string, unknown>)) {
    out[k] = isSensitiveCookieKey(k) ? REDACTED : v;
  }
  return out;
}

export function redactQueryString(qs: string): string {
  const params = new URLSearchParams(qs);
  for (const key of Array.from(params.keys())) {
    if (isSensitiveKey(key)) {
      params.set(key, REDACTED);
    }
  }
  return params.toString();
}
